import React, { memo, useCallback, useRef, useState } from 'react';

import { AutoComplete } from 'antd';

import { useCareerSkillsSearch, ICareerSkillOption } from 'core';

export interface ISkillAutocomplete {
  placeholder: string;
  category?: string;
  /** A dictionary skill picked from the dropdown. */
  onSelectSkill: (skill: ICareerSkillOption) => void;
  /** A typed value that matches no dictionary skill (spec 0018: goes to customSkills). */
  onFreeText?: (text: string) => void;
}

/**
 * Search-as-you-type picker backed by the `careerSkills` dictionary
 * (spec 0018, increment B). Reused by the skills section and the project
 * technologies field. Picking a suggestion emits the dictionary skill; pressing
 * Enter on a value that matches nothing falls through to `onFreeText`.
 */
export const SkillAutocomplete = memo(
  ({ placeholder, category, onSelectSkill, onFreeText }: ISkillAutocomplete) => {
    const [value, setValue] = useState('');
    const [search, { options }] = useCareerSkillsSearch();
    const debounceRef = useRef<ReturnType<typeof setTimeout>>();

    const onSearch = useCallback(
      (text: string) => {
        setValue(text);
        if (debounceRef.current) {
          clearTimeout(debounceRef.current);
        }
        debounceRef.current = setTimeout(() => search(text.trim(), category), 300);
      },
      [search, category],
    );

    const onSelect = useCallback(
      (skillId: string) => {
        const picked = options.find((option) => option.id === skillId);
        if (picked) {
          onSelectSkill(picked);
        }
        setValue('');
      },
      [options, onSelectSkill],
    );

    const onKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key !== 'Enter') {
          return;
        }
        const term = value.trim();
        if (!term) {
          return;
        }
        const exact = options.find((option) => option.name.toLowerCase() === term.toLowerCase());
        if (exact) {
          onSelectSkill(exact);
        } else if (onFreeText) {
          onFreeText(term);
        }
        setValue('');
      },
      [value, options, onSelectSkill, onFreeText],
    );

    return (
      <AutoComplete
        style={{ width: '100%' }}
        value={value}
        placeholder={placeholder}
        options={options.map((option) => ({ value: option.id, label: option.name }))}
        onSearch={onSearch}
        onSelect={onSelect}
        onKeyDown={onKeyDown}
      />
    );
  },
);
